import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { EnvironmentService } from '@patient-ui/shared-ui/utils';

import { AppointmentSlot } from '../models';

export interface AppointmentSlotsResponse {
  slots: AppointmentSlot[];
  nextAvailableDate: string | null;
}

@Injectable()
export class AppointmentSlotsService {
  constructor(
    private http: HttpClient,
    private environmentService: EnvironmentService
  ) {}

  loadSlots(
    locationId: number,
    serviceId: number,
    date: string
  ): Observable<AppointmentSlotsResponse> {
    const baseUrl = this.environmentService.appointmentSchedulingBaseUrl;

    const apiUrl = `${baseUrl}findAvailableAppointments?onsiteClient=false&x-client-type=patient&locationId=${locationId}&serviceId=${serviceId}&appointmentStartDate=${date}&noOfDays=1`;

    return this.http
      .get<unknown>(apiUrl, {
        headers: { Authorization: 'none' },
      })
      .pipe(
        map((data) =>
          this.transformApiResponse(data, locationId, serviceId, date)
        )
      );
  }

  private transformApiResponse(
    data: unknown,
    locationId: number,
    serviceId: number,
    selectedDate: string
  ): AppointmentSlotsResponse {
    const slots: AppointmentSlot[] = [];
    let nextAvailableDate: string | null = null;

    // Check if response is a direct array of appointments
    if (Array.isArray(data) && data.length > 0) {
      const firstAppointment = data[0] as Record<string, unknown>;
      if (firstAppointment.searchDateApptAvailable === false) {
        // No appointments for selected date, extract next available date
        nextAvailableDate = firstAppointment.apptDate as string;
      } else {
        // Filter and map only appointments that match the selected date
        data.forEach((apt: Record<string, unknown>) => {
          if (
            apt.apptDate === selectedDate ||
            (apt.isoApptDateTime as string)?.startsWith(selectedDate)
          ) {
            slots.push({
              date: apt.apptDate as string,
              time: apt.apptTime as string,
              dateTime: apt.isoApptDateTime as string,
              locationId: locationId,
              serviceId: serviceId,
              isAvailable: true,
              slotType: 'APPOINTMENT' as const,
            });
          }
        });
      }
    }
    // Check for nested structure (old format)
    else {
      const response = data as Record<string, unknown>;
      if (
        response.locationAppointmentAvailability &&
        Array.isArray(response.locationAppointmentAvailability) &&
        response.locationAppointmentAvailability.length > 0
      ) {
        const availability = response
          .locationAppointmentAvailability[0] as Record<string, unknown>;

        // Extract next available appointment date
        if (availability.nextAvailableAppointmentDate) {
          nextAvailableDate =
            availability.nextAvailableAppointmentDate as string;
        }

        if (
          availability.appointmentDates &&
          Array.isArray(availability.appointmentDates) &&
          availability.appointmentDates.length > 0
        ) {
          const dateSlots = availability.appointmentDates[0] as Record<
            string,
            unknown
          >;
          if (
            dateSlots.availableAppointments &&
            Array.isArray(dateSlots.availableAppointments) &&
            dateSlots.availableAppointments.length > 0
          ) {
            dateSlots.availableAppointments.forEach(
              (apt: Record<string, unknown>) => {
                slots.push({
                  date: selectedDate,
                  time: apt.appointmentTime as string,
                  dateTime: apt.appointmentDateTime as string,
                  locationId: locationId,
                  serviceId: serviceId,
                  isAvailable: true,
                  slotType: 'APPOINTMENT' as const,
                });
              }
            );
          }
        }
      }
    }

    return { slots, nextAvailableDate };
  }
}
